import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import { DOC_VALIDATION, ERROR_MESSAGES, REQUIRED_RULE } from 'utils/constants';
import GeneralApi from 'utils/generalApi';
import { IUser } from 'utils/interfaces';
import { useAuth } from 'contexts/Auth';
import { cleanObject } from 'utils/functions';

export default function UserModalContent({
  title,
  editData,
  onClose,
  onSuccess,
}: {
  title: string;
  editData?: IUser;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: editData || {
      document_type: 'DNI',
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);

  const documentType = watch('document_type');

  const submitAction = async (data: any) => {
    setLoading(true);
    let result: any = await (editData
      ? generalApi.put('/add_users', { ...cleanObject(data), country: 'PE' })
      : generalApi.post('/add_users', { ...cleanObject(data), country: 'PE' }));
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(editData ? 'Cambios guardados' : 'Usuario creado');
    onSuccess();
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        control={control}
        watch={watch}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  return (
    <form onSubmit={handleSubmit(submitAction)}>
      <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
        <div className="mt-3 text-center sm:mt-0 sm:text-left">
          <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
            {title || 'Confirmar acción'}
          </h3>
        </div>
      </div>
      <div className="space-y-4 px-4 py-5 sm:p-6">
        {buildBaseInput('Tipo de documento', 'select', 'document_type', REQUIRED_RULE, {
          options: [
            { value: 'DNI', name: 'DNI' },
            { value: 'CE', name: 'Carné de extranjería' },
            { value: 'PSP', name: 'Pasaporte' },
            { value: 'DM', name: 'Doc. Militar' },
          ],
        })}

        {buildBaseInput(
          'N° Documento',
          'text',
          'number',
          {
            ...REQUIRED_RULE,
            maxLength: {
              value: DOC_VALIDATION[documentType as keyof typeof DOC_VALIDATION]?.length,
              message: ERROR_MESSAGES.MAX_LENGTH(
                DOC_VALIDATION[documentType as keyof typeof DOC_VALIDATION]?.length
              ),
            },
          },
          {
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              DOC_VALIDATION[documentType as keyof typeof DOC_VALIDATION].validate(
                e.target.value,
                setValue,
                'number'
              ),
          }
        )}
        {buildBaseInput('Nombres', 'text', 'name', REQUIRED_RULE, {})}
        {buildBaseInput('Apellido Paterno', 'text', 'lname_p', REQUIRED_RULE, {})}
        {buildBaseInput('Apellido Materno', 'text', 'lname_m', REQUIRED_RULE, {})}
        {buildBaseInput('Correo', 'email', 'email', REQUIRED_RULE, {})}
      </div>
      <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <SolidButton
          className="w-auto sm:ml-3"
          isLoading={loading}
          onClick={() => handleSubmit(submitAction)}
        >
          {editData ? 'Guardar cambios' : 'Crear'}
        </SolidButton>
        <button
          type="button"
          onClick={onClose}
          className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
